/**
 * Deals API Service
 *
 * Provides methods for fetching coupons, offers, and merchant products
 * from the backend deals endpoints. Uses the shared api instance
 * which automatically adds Firebase auth tokens.
 */
import api from './backend';

/**
 * Fetch coupons from Rakuten via backend
 * @param {Object} filters - Filter options
 * @param {string} [filters.category] - Category filter
 * @param {string} [filters.keyword] - Keyword filter
 * @param {string} [filters.mid] - Merchant ID filter
 * @param {number} [filters.page] - Page number (default: 1)
 * @param {number} [filters.limit] - Results per page (default: 20)
 * @returns {Promise<Object>} - { success, coupons, total, page, limit, filters }
 */
export const getCoupons = async (filters = {}) => {
  const params = {};
  if (filters.category) params.category = filters.category;
  if (filters.keyword) params.keyword = filters.keyword;
  if (filters.mid) params.mid = filters.mid;
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;

  const response = await api.get('/api/deals/coupons', { params });
  return response.data;
};

/**
 * Fetch active offers from Rakuten via backend
 * @param {Object} filters - Filter options
 * @param {string} [filters.category] - Category filter
 * @param {string} [filters.advertiser_id] - Advertiser ID filter
 * @param {string} [filters.promotion_type] - Promotion type filter
 * @param {number} [filters.page] - Page number (default: 1)
 * @param {number} [filters.limit] - Results per page (default: 20)
 * @returns {Promise<Object>} - { success, offers, total, page, limit, filters }
 */
export const getOffers = async (filters = {}) => {
  const params = {};
  if (filters.category) params.category = filters.category;
  if (filters.advertiser_id) params.advertiser_id = filters.advertiser_id;
  if (filters.promotion_type) params.promotion_type = filters.promotion_type;
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;

  const response = await api.get('/api/deals/offers', { params });
  return response.data;
};

/**
 * Fetch product images for a specific merchant
 * Used to enrich deal cards with product imagery
 * @param {string} mid - Merchant/Advertiser ID
 * @param {number} [limit] - Max products to return (default: 5)
 * @returns {Promise<Object>} - { success, mid, products, count }
 */
export const getMerchantProducts = async (mid, limit = 5) => {
  const response = await api.get(`/api/deals/merchant-products/${mid}`, {
    params: { limit }
  });
  return response.data;
};

